import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useLocation, useNavigate } from "react-router";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { AvaliacaoOrdemCompra } from "../../domain/models/avaliacao_ordem_compra";
import { AvaliacaoOrdemCompraForm, AvaliacaoOrdemCompraSchema } from "../schemas/AvaliacaoFornecedor_cadastro/avaliacao_ordem_compra";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { useCentroCustoDrawer } from "../../../../common/components/drawers_api/drawers/centro_custo_drawer";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { ProdutoServicoDropDown } from "../../../../common/domain/models/produto_servico_dropdown";
import { AvaliacaoOrdemCompraColetaForm } from "../schemas/AvaliacaoFornecedor_cadastro/avaliacao_ordem_compra_coleta";
import { ProdutoServicoEntregue } from "../../domain/models/entrega_ordem_compra_produto_servico";
import { useAvaliacaoFornecedorCadastroPagination } from "../hooks/AvaliacaoFornecedor_cadastro/pagination";
import { ProdutoServicoParaEntrega } from "../../domain/models/produto_servicos_para_entrega";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { FornecedorDropDown } from "../../../../common/domain/models/fornecedor_dropdown";
import { EntradaProduto } from "../../../controleEstoque/domain/models/entrada_produtos_lista";
import { EntradaProdutoForm } from "../schemas/AvaliacaoFornecedor_cadastro/entrada_produtos";
import { useEntradaProdutoFilters } from "../../../controleEstoque/presentation/hooks/entrada_produto/filter";
import { useEntradaProdutoPagination } from "../../../controleEstoque/presentation/hooks/entrada_produto/pagination";

import dayjs from "dayjs";

/**
 * LoadEntregaProdutoServicoFuncState
 */

type LoadEntregaProdutoServicoFuncState = {
  id?: number;
  produtosServicos?: number[] | null;
  page?: number;
  rowsPerPage?: number;
};

const getIdOrdemCompra = (search: string) => {
  const value = new URLSearchParams(search).get('id');
  const id = value ? Number(value) : NaN;

  if (!Number.isSafeInteger(id) || id <= 0) {
    throw new Error('Falha ao buscar id de ordem de compra!');
  }

  return id;
};

/**
 * useAvaliacaoFornecedorCadastroViewModel
 */

export const useAvaliacaoFornecedorCadastroViewModel = () => {
  const {
    compras: {
      getProdutoServicoByIdOrdemCompraDropDownUseCase,
      getOrdemCompraAvaliacaoFornecedorUseCase,
      updateAvaliacaoOrdemCompraUseCase,
      createAvaliacaoOrdemCompraColetaUseCase,
      createEntradasEstoqueOrdemCompraUseCase,
      getAllEntregaProdutoServicoUseCase,
      deleteEntregaProdutoServicoUseCase,
      getAllOCProdutoServicosParaEntregaUseCase,
      downloadOrdemCompraArquivoUseCase,
      downloadEntregaOrdemCompraArquivoUseCase,
      downloadBoletoAvaliacaoOrdemCompraUseCase,
    },
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    },
    comercial: {
      createCentroCustoUseCase,
    },
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
    loadFornecedoresDropDown,
    loadFuncionariosDropDown,
  } = useDropDownLoader();

  const { pushErrorNotification, pushInfoNotification, setPageSaving, setPageLoading } = usePageLoader();

  const location = useLocation();
  const navigate = useNavigate();

  const backOrNavigate = useBackOrNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [funcionariosDropdown, setFuncionariosDropdown] = useState<FuncionarioDropDown[]>();
  const [produtosServicosDropDown, setProdutosServicosDropDown] = useState<ProdutoServicoDropDown[]>();

  const [idOrdemCompra, setIdOrdemCompra] = useState<number>();

  const [avaliacaoOrdemCompra, setAvaliacaoOrdemCompra] = useState<AvaliacaoOrdemCompra>();
  const [entregaProdutosServicos, setEntregaProdutosServicos] = useState<ProdutoServicoEntregue[]>();
  const [produtosServicosParaEntrega, setProdutosServicosParaEntrega] = useState<ProdutoServicoParaEntrega[]>();

  const [selectedProdutosServicosQuery, setSelectedProdutosServicosQuery] = useState<ProdutoServicoDropDown[] | null>(null);
  const [selectedProdutosServicos, setSelectedProdutosServicos] = useState<ProdutoServicoEntregue>();

  const [avaliacaoOrdemCompraColetaModal, setAvaliacaoOrdemCompraColetaModal] = useState(false);
  const [produtoServicoDeleteModal, setProdutoServicoDeleteModal] = useState(false);

  const [entradaMultiModal, setEntradaMultiModal] = useState(false);
  const [selectedEntradaProduto] = useState<EntradaProduto>();
  const [entradaProdutosEstoque, setEntradaProdutosEstoque] = useState<EntradaProduto[]>([]);
  const [centroCustoEstoqueLista, setCentroCustoEstoqueLista] = useState<CentroCustoDropDown[]>([]);
  const [fornecedoresEstoqueLista, setFornecedoresEstoqueLista] = useState<FornecedorDropDown[]>([]);
  const [selectedProdutosServicosEstoque, setSelectedProdutosServicosEstoque] = useState<ProdutoServicoDropDown[] | null>(null);
  const [centroCustoDrawerState, startCentroCustoDrawer] = useCentroCustoDrawer();

  const pagination = useAvaliacaoFornecedorCadastroPagination();

  const hookForm = useForm<AvaliacaoOrdemCompraForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(AvaliacaoOrdemCompraSchema),
  });

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: message => pushErrorNotification('Erro', message),
  });

  const [criterioItems, statusEntrega] = hookForm.watch(['criterioItems', 'statusEntrega']);

  const notaMedia = useMemo(() => {
    const notas = (criterioItems ?? []).filter(x => x.nota !== null && x.nota > 0);
    return notas.length > 0
      ? notas.reduce((total, x) => total + (x.nota ?? 0), 0) / notas.length
      : 0;
  }, [criterioItems]);

  const filteredFuncionariosDropdown = useMemo(() => {
    return funcionariosDropdown
      ? funcionariosDropdown.filter(x => {
        if (avaliacaoOrdemCompra) {
          if (avaliacaoOrdemCompra.idFuncionarioAvaliador === x.idFuncionario) {
            return true;
          }
        }
        return x.canBeSelected === true;
      })
      : null;
  }, [
    avaliacaoOrdemCompra,
    funcionariosDropdown,
  ]);

  const statusEntregaOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Entregue totalmente',
        style: {
          backgroundColor: '#45b6af',
          color: 'white',
          padding: '.2rem .6rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '15px',
        },
      },
      {
        id: 2,
        label: 'Entrega Parcial',
        style: {
          backgroundColor: '#f8cb00',
          color: 'white',
          padding: '.2rem .6rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '15px',
        },
      },
      {
        id: 3,
        label: 'Não Entregue',
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .6rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '15px',
        },
      },
    ];
  }, [
  ]);

  /**
   * Funcoes de carregamento
   */

  const loadProdutosServicosDropdown = useCallback(async (id?: number) => {
    try {
      const filteredIdOrdemCompra = id ?? idOrdemCompra;
      const results = await getProdutoServicoByIdOrdemCompraDropDownUseCase
        .getProdutoServicoByIdOrdemCompraDropDown(filteredIdOrdemCompra);
      setProdutosServicosDropDown(results);
    } catch (e: unknown) {
      setProdutosServicosDropDown(undefined);
      throw new Error('Falha ao buscar produtos/serviços!');
    }
  }, [
    idOrdemCompra,
    getProdutoServicoByIdOrdemCompraDropDownUseCase,
  ]);

  const loadAvaliacaoOrdemCompra = useCallback(async (id?: number, preserveForm = false) => {
    try {
      const filteredIdOrdemCompra = id ?? idOrdemCompra;
      const result = await getOrdemCompraAvaliacaoFornecedorUseCase
        .getOrdemCompraAvaliacaoFornecedor(filteredIdOrdemCompra);

      if (preserveForm) {
        setAvaliacaoOrdemCompra(prev => prev ? ({
          ...result,
          nomeArquivo: prev.nomeArquivo,
          nomeArquivoBoleto: prev.nomeArquivoBoleto,
        }) : result);
        return;
      }

      if (result.entregue === true) {
        hookForm.setValue('statusEntrega', 1);
      } else {
        if (result.entregaParcial === true) {
          hookForm.setValue('statusEntrega', 2);
        }
        else {
          hookForm.setValue('statusEntrega', 3);
        }
      }

      if (result.notaFiscalEntregue === true) {
        hookForm.setValue('statusNfEntrega', 1);
      } else {
        hookForm.setValue('statusNfEntrega', 0);
      }

      if (result.idFuncionarioAvaliador !== null) {
        hookForm.setValue('avaliadoPor', result.idFuncionarioAvaliador);
      } else {
        hookForm.resetField('avaliadoPor');
      }

      if (result.data !== null) {
        hookForm.setValue('data', dayjs.utc(result.data).local().toDate());
      } else {
        hookForm.resetField('data');
      }

      hookForm.setValue('nroNotaFiscal', result.nrNotaFiscal);
      hookForm.setValue('observacao', result.observacao);
      hookForm.setValue('criterioItems', result.criterioItens.map(x => ({
        idCriterioAvaliacaoFornecedor: x.idCriterioAvaliacaoFornecedor,
        idItemAvaliacaoFornecedor: x.idItemAvaliacaoFornecedor,
        descricao: x.descricao,
        ordem: x.ordem,
        nota: x.nota,
      })));

      setAvaliacaoOrdemCompra(result);
    } catch (e: unknown) {
      if (!preserveForm) setAvaliacaoOrdemCompra(undefined);
      throw new Error('Falha ao buscar criterios de avaliação de fornecedores!');
    }
  }, [
    idOrdemCompra,
    hookForm,
    getOrdemCompraAvaliacaoFornecedorUseCase,
  ]);

  const loadEntregaProdutoServico = useCallback(async (fs?: LoadEntregaProdutoServicoFuncState) => {
    try {
      const filteredIdOrdemCompra = fs?.id ?? idOrdemCompra;
      const filteredProdutosServicos = fs?.produtosServicos !== undefined
        ? fs.produtosServicos
        : selectedProdutosServicosQuery?.map(x => x.idProdutoServico) ?? null;

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllEntregaProdutoServicoUseCase
        .getAllEntregaProdutoServico(
          filteredIdOrdemCompra,
          filteredProdutosServicos,
          filteredPage,
          filteredRowsPerPage,
        );

      setEntregaProdutosServicos(getAllEntregaProdutoServicoUseCase.items);
      pagination.setTotalItems(getAllEntregaProdutoServicoUseCase.totalItems);
    } catch (e: unknown) {
      setEntregaProdutosServicos(undefined);
      throw new Error('Falha ao buscar criterios de avaliação de fornecedores!');
    }
  }, [
    idOrdemCompra,
    selectedProdutosServicosQuery,
    pagination,
    getAllEntregaProdutoServicoUseCase,
  ]);

  const loadOCProdutoServicosParaEntrega = useCallback(async (id?: number) => {
    try {
      const filteredIdOrdemCompra = id ?? idOrdemCompra;

      const results = await getAllOCProdutoServicosParaEntregaUseCase
        .getAllOCProdutoServicosParaEntrega(filteredIdOrdemCompra);

      setProdutosServicosParaEntrega(results);
    } catch (e: unknown) {
      setProdutosServicosParaEntrega(undefined);
      throw new Error('Falha ao buscar produtos/serviços para entrega!');
    }
  }, [getAllOCProdutoServicosParaEntregaUseCase, idOrdemCompra]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const id = getIdOrdemCompra(location.search);

      setIdOrdemCompra(id);

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadProdutosServicosDropdown(id);

      await loadAvaliacaoOrdemCompra(id);

      await loadEntregaProdutoServico({ id });

      await loadOCProdutoServicosParaEntrega(id);

    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      const id = getIdOrdemCompra(location.search);
      setIdOrdemCompra(id);

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadProdutosServicosDropdown(id);

      await loadAvaliacaoOrdemCompra(id);

      await loadEntregaProdutoServico({ id });

      await loadOCProdutoServicosParaEntrega(id);

      setErrorMessage(undefined);

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    location.search,
    loadFuncionariosDropDown,
    loadProdutosServicosDropdown,
    loadAvaliacaoOrdemCompra,
    loadEntregaProdutoServico,
    loadOCProdutoServicosParaEntrega,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/OrdemCompras_entrega' });
  }, [
    backOrNavigate,
  ]);

  const handleAvaliacaoOrdemCompraSubmit = useCallback(async (form: AvaliacaoOrdemCompraForm) => {
    if (idOrdemCompra) {
      try {
        setSaving(true);
        setPageSaving(true);

        if (form.statusEntrega === 1 && form.data === null) {
          hookForm.setError('data', { message: 'Você deve preencher este campo!' });
          return;
        }

        await updateAvaliacaoOrdemCompraUseCase.updateAvaliacaoOrdemCompra(
          idOrdemCompra,
          form.statusEntrega,
          form.statusNfEntrega,
          form.data,
          form.avaliadoPor,
          form.nroNotaFiscal,
          form.observacao,
          form.arquivo,
          form.limparArquivo,
          form.arquivoBoleto,
          form.limparArquivoBoleto,
          form.criterioItems,
        );

        if (form.statusEntrega === 1 && form.data !== null) {
          // Consulta as quantidades atuais para não duplicar entregas já registradas.
          const produtosParaEntrega = await getAllOCProdutoServicosParaEntregaUseCase
            .getAllOCProdutoServicosParaEntrega(idOrdemCompra);
          const produtosServicos = produtosParaEntrega
            .filter(item => item.restante !== null && item.restante > 0)
            .map(item => ({
              idProdutoServico: item.idProdutoServico,
              quantidadeRecebida: item.restante,
              quantidadeRestante: item.restante,
            }));

          if (produtosServicos.length > 0) {
            await createAvaliacaoOrdemCompraColetaUseCase.createAvaliacaoOrdemCompraColeta(
              idOrdemCompra,
              form.nroNotaFiscal ?? null,
              form.data,
              form.limparArquivo ? null : form.arquivo,
              produtosServicos,
              false,
            );
          }
        }

        await loadAvaliacaoOrdemCompra();
        await loadEntregaProdutoServico();
        await loadOCProdutoServicosParaEntrega();

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao salvar avaliação ou registrar entregas!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idOrdemCompra,
    hookForm,
    updateAvaliacaoOrdemCompraUseCase,
    getAllOCProdutoServicosParaEntregaUseCase,
    createAvaliacaoOrdemCompraColetaUseCase,
    loadAvaliacaoOrdemCompra,
    loadEntregaProdutoServico,
    loadOCProdutoServicosParaEntrega,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleAvaliacaoOrdemCompraColetaOpenState = useCallback((openState: boolean) => {
    setAvaliacaoOrdemCompraColetaModal(openState);
  }, [
  ]);

  const handleAvaliacaoOrdemCompraColetaSubmit = useCallback(async (form: AvaliacaoOrdemCompraColetaForm) => {
    if (idOrdemCompra) {
      try {
        setSaving(true);
        setPageSaving(true);

        await createAvaliacaoOrdemCompraColetaUseCase.createAvaliacaoOrdemCompraColeta(
          idOrdemCompra,
          form.nroNotaFiscal,
          form.dataEntrega,
          form.arquivo,
          form.produtosServicos,
          form.registrarEntradaEstoque,
        );

        await loadAvaliacaoOrdemCompra(undefined, true);
        await loadEntregaProdutoServico();
        await loadOCProdutoServicosParaEntrega();

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');

        setAvaliacaoOrdemCompraColetaModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idOrdemCompra,
    createAvaliacaoOrdemCompraColetaUseCase,
    loadAvaliacaoOrdemCompra,
    loadEntregaProdutoServico,
    loadOCProdutoServicosParaEntrega,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleProdutoServicoDeleteOpenState = useCallback((idProdutoServicoEntregue: number | null, openState: boolean) => {
    if (openState) {
      if (idProdutoServicoEntregue && entregaProdutosServicos) {
        const produtoServicoEntregue = entregaProdutosServicos.find(x => x.idProdutoServicoEntregue === idProdutoServicoEntregue);
        setSelectedProdutosServicos(produtoServicoEntregue);
      } else {
        setSelectedProdutosServicos(undefined);
      }
    }
    setProdutoServicoDeleteModal(openState);
  }, [
    entregaProdutosServicos,
  ]);

  const handleProdutoServicoDeleteSubmit = useCallback(async () => {
    if (idOrdemCompra && selectedProdutosServicos) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteEntregaProdutoServicoUseCase
          .deleteEntregaProdutoServico(
            idOrdemCompra,
            selectedProdutosServicos.idProdutoServicoEntregue,
          );

        await loadAvaliacaoOrdemCompra(undefined, true);
        await loadEntregaProdutoServico();
        await loadOCProdutoServicosParaEntrega();

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');

        setProdutoServicoDeleteModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idOrdemCompra,
    selectedProdutosServicos,
    deleteEntregaProdutoServicoUseCase,
    loadEntregaProdutoServico,
    loadAvaliacaoOrdemCompra,
    loadOCProdutoServicosParaEntrega,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleAvaliacaoNotaChange = useCallback((idCriterioAvaliacaoFornecedor: number, nota: number | null) => {
    const criterioItems = hookForm.getValues('criterioItems');
    const itemIdx = criterioItems.findIndex(x => x.idCriterioAvaliacaoFornecedor === idCriterioAvaliacaoFornecedor);

    if (itemIdx >= 0) {
      hookForm.setValue('criterioItems', criterioItems.map((item, index) =>
        index === itemIdx ? { ...item, nota } : item), { shouldDirty: true });
    }
  }, [
    hookForm,
  ]);

  const handleProdutosServicosQueryChange = useCallback(async (items: ProdutoServicoDropDown[] | null) => {
    try {
      setLoading(true);
      useAvaliacaoFornecedorCadastroPagination.setState({ page: 1 });
      await loadEntregaProdutoServico({ produtosServicos: items?.length ? items.map(x => x.idProdutoServico) : null, page: 1 });
      setSelectedProdutosServicosQuery(items);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadEntregaProdutoServico,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useAvaliacaoFornecedorCadastroPagination.setState({ page: page + 1 });
      await loadEntregaProdutoServico({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadEntregaProdutoServico,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useAvaliacaoFornecedorCadastroPagination.setState({ page: 1, rowsPerPage });
      await loadEntregaProdutoServico({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadEntregaProdutoServico,
    pushErrorNotification,
  ]);

  const handleOpenChangeHistory = useCallback((idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
    if (idTabela && tipoTabela) {
      changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    } else {
      if (idOrdemCompra) {
        changeHistory.handleOpenChangeHistory(idOrdemCompra, LogTipoTabela.ORDEM_COMPRA, true);
      }
    }
  }, [
    idOrdemCompra,
    changeHistory,
  ]);

  const handleNotaFiscalDownload = useCallback(async (nomeArquivo: string) => {
    if (idOrdemCompra) {
      try {
        setLoading(true);
        setPageLoading(true);
        await downloadOrdemCompraArquivoUseCase
          .downloadOrdemCompraArquivo(idOrdemCompra, nomeArquivo);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao baixar arquivo!'));
      } finally {
        setLoading(false);
        setPageLoading(false);
      }
    }
  }, [
    idOrdemCompra,
    downloadOrdemCompraArquivoUseCase,
    pushErrorNotification,
    setPageLoading,
  ]);

  const handleNotaFiscalDelete = useCallback(() => {
    setAvaliacaoOrdemCompra(prev => prev ? ({ ...prev, nomeArquivo: null }) : prev);
  }, [
  ]);


  const handleEntregaNotaFiscalDownload = useCallback(async (idProdutoServicoEntregue: number, nomeArquivo: string) => {
    if (idOrdemCompra) {
      try {
        setLoading(true);
        setPageLoading(true);
        await downloadEntregaOrdemCompraArquivoUseCase
          .downloadEntregaOrdemCompraArquivo(idOrdemCompra, idProdutoServicoEntregue, nomeArquivo);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao baixar arquivo!'));
      } finally {
        setLoading(false);
        setPageLoading(false);
      }
    }
  }, [
    idOrdemCompra,
    downloadEntregaOrdemCompraArquivoUseCase,
    pushErrorNotification,
    setPageLoading,
  ]);

  const handleBoletoDelete = useCallback(() => {
    setAvaliacaoOrdemCompra(prev => prev ? ({ ...prev, nomeArquivoBoleto: null }) : prev);
  }, [
  ]);

  const handleBoletoDownload = useCallback(async (nomeArquivo: string) => {
    if (idOrdemCompra) {
      try {
        setLoading(true);
        setPageLoading(true);
        await downloadBoletoAvaliacaoOrdemCompraUseCase
          .downloadBoletoAvaliacaoOrdemCompra(idOrdemCompra, nomeArquivo);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao baixar arquivo!'));
      } finally {
        setLoading(false);
        setPageLoading(false);
      }
    }
  }, [
    idOrdemCompra,
    downloadBoletoAvaliacaoOrdemCompraUseCase,
    pushErrorNotification,
    setPageLoading,
  ]);

  const handleVerEntradasEstoque = useCallback(() => {
    if (!idOrdemCompra) return;

    useEntradaProdutoFilters.getState().clearFilters();
    useEntradaProdutoFilters.setState({ ordemCompra: [idOrdemCompra] });
    useEntradaProdutoPagination.setState({ page: 1 });
    navigate('/EntradaProdutos_lista/');
  }, [idOrdemCompra, navigate]);

  const handleEntradaMultiModalOpenState = useCallback(async (open: boolean) => {
    if (!open) {
      setEntradaMultiModal(false);
      return;
    }

    if (!idOrdemCompra || !avaliacaoOrdemCompra) return;

    setEntradaMultiModal(true);
    setEntradaProdutosEstoque([]);
    setLoading(true);

    try {
      await Promise.all([
        loadCentroCustoDropDown(items => setCentroCustoEstoqueLista(items)),
        loadFornecedoresDropDown(items => setFornecedoresEstoqueLista(items)),
      ]);

      const rowsPerPage = 100;
      await getAllEntregaProdutoServicoUseCase.getAllEntregaProdutoServico(
        idOrdemCompra,
        null,
        1,
        rowsPerPage,
      );

      const entregas = [...getAllEntregaProdutoServicoUseCase.items];
      const totalPages = getAllEntregaProdutoServicoUseCase.totalPages;

      for (let page = 2; page <= totalPages; page += 1) {
        await getAllEntregaProdutoServicoUseCase.getAllEntregaProdutoServico(
          idOrdemCompra,
          null,
          page,
          rowsPerPage,
        );
        entregas.push(...getAllEntregaProdutoServicoUseCase.items);
      }

      const dataAvaliacao = avaliacaoOrdemCompra.data
        ? dayjs.utc(avaliacaoOrdemCompra.data).local().toDate()
        : new Date();

      const produtosEntregues = entregas
        .filter(item => item.tipo?.toUpperCase() === 'P'
          && item.idProdutoServico !== null
          && (item.quantidadeEntregue ?? 0) > 0)
        .map(item => {
          const quantidade = item.quantidadeEntregue ?? 0;
          const valorUnitario = item.valorUnitario;

          return {
            IdEntradaProduto: item.idProdutoServicoEntregue,
            IdOrdemCompra: null,
            NroOrdemCompra: null,
            Data: item.dataEntrega
              ? dayjs.utc(item.dataEntrega).local().toDate()
              : dataAvaliacao,
            IdCentroCusto: avaliacaoOrdemCompra.idCentroCusto,
            IdProdutoServico: item.idProdutoServico,
            NroNotaFiscal: item.nroNotaFiscal ?? avaliacaoOrdemCompra.nrNotaFiscal,
            Quantidade: quantidade,
            IdFornecedor: avaliacaoOrdemCompra.idFornecedor,
            IdOrdemCompraProduto: item.idOrdemCompraProdutoServico,
            NrNotaFiscal: item.nroNotaFiscal ?? avaliacaoOrdemCompra.nrNotaFiscal,
            IdSessaoMultipla: null,
            ValorUnitario: valorUnitario,
            codigo: null,
            NomeFornecedor: avaliacaoOrdemCompra.fornecedor,
            NomeProdutoServico: item.descricao,
            NomeCentroCusto: avaliacaoOrdemCompra.centroCusto,
            ValorTotal: valorUnitario !== null ? quantidade * valorUnitario : null,
            Unidade: item.unidade,
          } satisfies EntradaProduto;
        });

      setEntradaProdutosEstoque(produtosEntregues);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao carregar produtos entregues para entrada no estoque!'));
    } finally {
      setLoading(false);
    }
  }, [
    idOrdemCompra,
    avaliacaoOrdemCompra,
    loadCentroCustoDropDown,
    loadFornecedoresDropDown,
    getAllEntregaProdutoServicoUseCase,
    pushErrorNotification,
  ]);

  const handleProdutosServicosEstoqueQueryChange = useCallback((items: ProdutoServicoDropDown[] | null) => {
    setSelectedProdutosServicosEstoque(items);
  }, []);

  const handleCreateCentroCustoEstoque = useCallback((onCreated: (idCentroCusto: number) => void) => {
    startCentroCustoDrawer(async form => {
      try {
        setSaving(true);
        setPageSaving(true);

        const { idCentroCusto } = await createCentroCustoUseCase.createCentroCusto(
          form.status,
          form.codigo,
          form.descricao,
          form.bdi,
          form.bdi2,
          form.numeroCno ?? undefined,
          form.obsCentroCusto ?? undefined,
        );

        await loadCentroCustoDropDown(items => setCentroCustoEstoqueLista(items));
        onCreated(idCentroCusto);
        pushInfoNotification('Sucesso', 'Centro de custo cadastrado com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao cadastrar centro de custo!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    });
  }, [
    startCentroCustoDrawer,
    createCentroCustoUseCase,
    loadCentroCustoDropDown,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleEntradaProdutoMultiSubmit = useCallback(async (form: EntradaProdutoForm) => {
    const produtos = entradaProdutosEstoque.filter(item =>
      item.IdProdutoServico !== null
      && item.Quantidade !== null
      && item.Quantidade !== undefined
      && item.Quantidade > 0,
    );

    if (produtos.length === 0) {
      pushErrorNotification('Atenção', 'Não há produtos entregues para cadastrar no estoque.');
      return;
    }

    if (form.IdCentroCusto === null || form.IdCentroCusto === undefined) {
      pushErrorNotification('Atenção', 'Selecione um centro de custo para cadastrar as entradas.');
      return;
    }

    if (!idOrdemCompra) {
      pushErrorNotification('Erro', 'Não foi possível identificar a ordem de compra.');
      return;
    }

    try {
      setSaving(true);
      setPageSaving(true);

      await createEntradasEstoqueOrdemCompraUseCase.createEntradasEstoqueOrdemCompra(
        idOrdemCompra,
        form.IdCentroCusto,
        form.Data ?? null,
        produtos.map(item => item.IdEntradaProduto),
      );

      await loadAvaliacaoOrdemCompra(undefined, true);

      pushInfoNotification('Sucesso', 'Entradas de estoque cadastradas com sucesso!');
      setEntradaProdutosEstoque([]);
      setEntradaMultiModal(false);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao cadastrar entradas no estoque!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    entradaProdutosEstoque,
    idOrdemCompra,
    createEntradasEstoqueOrdemCompraUseCase,
    loadAvaliacaoOrdemCompra,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleEntradaProdutoOpenChangeHistory = useCallback<(id: number, tipoTabela: LogTipoTabela) => void>(() => {
    // TODO: abrir o histórico da entrada do estoque.
  }, []);

  const handleStatusEntregaChange = useCallback(async (value: number | null, prevValue: number | null) => {
    const validacao = await hookForm.trigger();
    if (!validacao && prevValue !== null) {
      hookForm.setValue('statusEntrega', prevValue);
    } else {
      hookForm.handleSubmit(handleAvaliacaoOrdemCompraSubmit)();
    }
  }, [
    hookForm,
    handleAvaliacaoOrdemCompraSubmit,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    funcionariosDropdown: filteredFuncionariosDropdown ?? [],
    produtosServicosDropDown: produtosServicosDropDown ?? [],
    idOrdemCompra,
    pagination,
    hookForm,
    changeHistory,
    avaliacaoOrdemCompra: avaliacaoOrdemCompra ?? null,
    entregaProdutosServicos: entregaProdutosServicos ?? [],
    produtosServicosParaEntrega: produtosServicosParaEntrega ?? [],
    selectedProdutosServicosQuery,
    selectedProdutosServicos,
    avaliacaoOrdemCompraColetaModal,
    produtoServicoDeleteModal,
    entradaMultiModal,
    centroCustoDrawerState,
    selectedEntradaProduto,
    entradaProdutosEstoque,
    centroCustoEstoqueLista,
    fornecedoresEstoqueLista,
    selectedProdutosServicosEstoque,
    criterioItems: criterioItems ?? [],
    statusEntrega,
    notaMedia,
    statusEntregaOptions,
    handleReload,
    handleBack,
    handleAvaliacaoOrdemCompraSubmit: hookForm.handleSubmit(handleAvaliacaoOrdemCompraSubmit),
    handleAvaliacaoOrdemCompraColetaOpenState,
    handleAvaliacaoOrdemCompraColetaSubmit,
    handleProdutoServicoDeleteOpenState,
    handleProdutoServicoDeleteSubmit,
    handleAvaliacaoNotaChange,
    handleProdutosServicosQueryChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenChangeHistory,
    handleNotaFiscalDownload,
    handleNotaFiscalDelete,
    handleEntregaNotaFiscalDownload,
    handleBoletoDelete,
    handleBoletoDownload,
    handleVerEntradasEstoque,
    handleEntradaMultiModalOpenState,
    handleCreateCentroCustoEstoque,
    handleProdutosServicosEstoqueQueryChange,
    handleEntradaProdutoMultiSubmit,
    handleEntradaProdutoOpenChangeHistory,
    handleStatusEntregaChange,
  };
}
